import { TagSelectionChip } from './TagSelectionChip';
import { SelectionField } from '../../components/ui/SelectionField';
import { useEffect, useRef } from 'react';
import { Combobox } from '../../components/ui/Combobox';
import { TagPill } from './TagPill';
import { inventoryKey } from '../collection/multiCollectionIdentity';
import { InventoryTag } from './useCollectionTags';

export const inventoryTagKey = (tag: InventoryTag) =>
  inventoryKey(tag.collectionId, tag.tagId);
export const inventoryTagLabel = (tag: InventoryTag) =>
  tag.collectionName ? `${tag.collectionName}/${tag.name}` : tag.name;

export function TagFilter({
  value,
  tags,
  onChange,
}: {
  value: string[];
  tags: InventoryTag[];
  onChange: (value: string[]) => void;
}) {
  const byKey = new Map(tags.map((tag) => [inventoryTagKey(tag), tag]));
  const knownTags = useRef(new Map<string, InventoryTag>());
  useEffect(() => {
    tags.forEach((tag) => {
      knownTags.current.set(inventoryTagKey(tag), tag);
    });
  }, [tags]);
  const getLabel = (key: string) => {
    const tag = byKey.get(key) || knownTags.current.get(key);
    return tag ? inventoryTagLabel(tag) : 'Selected tag';
  };
  const grouped = tags.some((tag) => tag.collectionName);
  return (
    <SelectionField label="Tags">
      <Combobox<string>
        multiple
        label="Tags"
        placeholder="Type to search tags…"
        sx={{ maxWidth: '100%' }}
        getOptionKey={(key) => key}
        renderOptionContent={(key) => {
          const tag = byKey.get(key) || knownTags.current.get(key);
          return tag ? <TagPill variant="body1" tag={tag} /> : getLabel(key);
        }}
        addLabel="Add tag"
        inputAriaLabel="Filter tags"
        options={tags.map(inventoryTagKey)}
        value={value}
        onChange={onChange}
        getOptionLabel={getLabel}
        groupBy={grouped ? (key) => byKey.get(key)!.collectionId : undefined}
        getGroupLabel={(collectionId) =>
          tags.find((tag) => tag.collectionId === collectionId)
            ?.collectionName || 'Collection'
        }
      />
      {value.map((key) => (
        <TagSelectionChip
          key={key}
          label={getLabel(key)}
          tagId={(byKey.get(key) || knownTags.current.get(key))?.tagId}
          onRemove={() =>
            onChange(value.filter((candidate) => candidate !== key))
          }
        />
      ))}
    </SelectionField>
  );
}
